import { useState, useEffect } from 'react';
import { Check, X, Eye, Trash2, Search, Plus, Edit } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyTalent = {
  name: '',
  email: '',
  phone: '',
  bio: '',
  age_range: '',
  height: '',
  weight: '',
  skills: [],
  representation_type: '',
  demo_reel_url: '',
  resume_url: '',
  headshot_url: '',
  instagram: '',
  imdb: '',
  website: ''
};

export default function AdminTalents() {
  const [talents, setTalents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTalent, setSelectedTalent] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(emptyTalent);
  const [skillInput, setSkillInput] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchTalents();
  }, [filter]);

  const fetchTalents = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const url = filter === 'all' 
        ? `${API}/admin/talents` 
        : `${API}/admin/talents?status=${filter}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTalents(response.data);
    } catch (error) {
      console.error('Error fetching talents:', error);
      toast.error('Failed to load talents');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (talentId, status) => {
    const token = localStorage.getItem('admin_token');
    try {
      await axios.patch(`${API}/admin/talents/${talentId}/status`, 
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Talent ${status}!`);
      fetchTalents();
      setShowModal(false);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const deleteTalent = async (talentId) => {
    if (!window.confirm('Are you sure you want to delete this talent?')) return;
    
    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(`${API}/admin/talents/${talentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Talent deleted');
      fetchTalents();
      setShowModal(false);
    } catch (error) {
      toast.error('Failed to delete talent');
    }
  };

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const addSkill = () => {
    if (skillInput.trim() && !formData.skills.includes(skillInput.trim())) {
      setFormData({ ...formData, skills: [...formData.skills, skillInput.trim()] });
      setSkillInput('');
    }
  };

  const removeSkill = (skill) => {
    setFormData({ ...formData, skills: formData.skills.filter(s => s !== skill) });
  };

  const handleAddTalent = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const token = localStorage.getItem('admin_token');

    try {
      await axios.post(`${API}/admin/talents/create`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Talent added successfully!');
      setShowAddModal(false);
      setFormData(emptyTalent);
      fetchTalents();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to add talent';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditTalent = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const token = localStorage.getItem('admin_token');

    try {
      await axios.put(`${API}/admin/talents/${selectedTalent.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Talent updated successfully!');
      setShowAddModal(false);
      setIsEditing(false);
      setFormData(emptyTalent);
      setSelectedTalent(null);
      fetchTalents();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update talent';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const openEditModal = (talent) => {
    setFormData({
      name: talent.name || '',
      email: talent.email || '',
      phone: talent.phone || '',
      bio: talent.bio || '',
      age_range: talent.age_range || '',
      height: talent.height || '',
      weight: talent.weight || '',
      skills: talent.skills || [],
      representation_type: talent.representation_type || '',
      demo_reel_url: talent.demo_reel_url || '',
      resume_url: talent.resume_url || '',
      headshot_url: talent.headshot_url || '',
      instagram: talent.instagram || '',
      imdb: talent.imdb || '',
      website: talent.website || ''
    });
    setSelectedTalent(talent);
    setIsEditing(true);
    setShowAddModal(true);
    setShowModal(false);
  };

  const filteredTalents = talents.filter(t => 
    t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getBadgeClass = (status) => {
    switch (status) {
      case 'approved': return 'badge badge-approved';
      case 'rejected': return 'badge badge-rejected';
      default: return 'badge badge-pending';
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-talents-page">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
          <h1 style={{ fontSize: '1.75rem', fontFamily: 'Playfair Display, serif', margin: 0 }}>
            Talent Management
          </h1>
          <button
            onClick={() => { setFormData(emptyTalent); setIsEditing(false); setShowAddModal(true); }}
            className="btn btn-primary"
            data-testid="add-talent-btn"
          >
            <Plus size={18} /> Add Talent
          </button>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {['all', 'pending', 'approved', 'rejected'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`btn ${filter === f ? 'btn-primary' : 'btn-ghost'}`}
                style={{ padding: '0.5rem 1rem', textTransform: 'capitalize' }}
                data-testid={`filter-${f}`}
              >
                {f}
              </button>
            ))}
          </div>
          <div style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
            <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-input"
              style={{ paddingLeft: '40px' }}
              data-testid="search-input"
            />
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="loading">
            <div className="spinner" />
          </div>
        ) : filteredTalents.length === 0 ? (
          <div className="card" style={{ padding: '3rem', textAlign: 'center' }}>
            <p style={{ color: 'var(--text-secondary)' }}>No talents found</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTalents.map((talent) => (
                  <tr key={talent.id} data-testid={`talent-row-${talent.id}`}>
                    <td data-label="Name">{talent.name}</td>
                    <td data-label="Email">{talent.email}</td>
                    <td data-label="Type">{talent.representation_type || 'N/A'}</td>
                    <td data-label="Status">
                      <span className={getBadgeClass(talent.status)}>
                        {talent.status}
                      </span>
                    </td>
                    <td data-label="Date">
                      {new Date(talent.created_at).toLocaleDateString()}
                    </td>
                    <td data-label="Actions">
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          onClick={() => { setSelectedTalent(talent); setShowModal(true); }}
                          className="action-btn"
                          title="View Details"
                          data-testid={`view-talent-${talent.id}`}
                        >
                          <Eye size={18} />
                        </button>
                        <button
                          onClick={() => openEditModal(talent)}
                          className="action-btn"
                          title="Edit"
                          data-testid={`edit-talent-${talent.id}`}
                        >
                          <Edit size={18} />
                        </button>
                        {talent.status === 'pending' && (
                          <>
                            <button
                              onClick={() => updateStatus(talent.id, 'approved')}
                              className="action-btn approve"
                              title="Approve"
                              data-testid={`approve-talent-${talent.id}`}
                            >
                              <Check size={18} />
                            </button>
                            <button
                              onClick={() => updateStatus(talent.id, 'rejected')}
                              className="action-btn reject"
                              title="Reject"
                              data-testid={`reject-talent-${talent.id}`}
                            >
                              <X size={18} />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => deleteTalent(talent.id)}
                          className="action-btn reject"
                          title="Delete"
                          data-testid={`delete-talent-${talent.id}`}
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* View Modal */}
        {showModal && selectedTalent && (
          <div className="modal-overlay" onClick={() => setShowModal(false)} data-testid="talent-detail-modal">
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2 className="modal-title">{selectedTalent.name}</h2>
              
              <div style={{ marginBottom: '1rem' }}>
                <span className={getBadgeClass(selectedTalent.status)} style={{ marginBottom: '1rem', display: 'inline-block' }}>
                  {selectedTalent.status}
                </span>
              </div>

              <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                <p><strong>Email:</strong> {selectedTalent.email}</p>
                <p><strong>Phone:</strong> {selectedTalent.phone || 'N/A'}</p>
                <p><strong>Type:</strong> {selectedTalent.representation_type || 'N/A'}</p>
                <p><strong>Age Range:</strong> {selectedTalent.age_range || 'N/A'}</p>
                <p><strong>Height:</strong> {selectedTalent.height || 'N/A'}</p>
                {selectedTalent.bio && (
                  <div style={{ marginTop: '1rem' }}>
                    <strong>Bio:</strong>
                    <p style={{ marginTop: '0.5rem', whiteSpace: 'pre-wrap' }}>{selectedTalent.bio}</p>
                  </div>
                )}
                {selectedTalent.skills && selectedTalent.skills.length > 0 && (
                  <div style={{ marginTop: '1rem' }}>
                    <strong>Skills:</strong>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                      {selectedTalent.skills.map((skill, idx) => (
                        <span key={idx} className="skill-tag">{skill}</span>
                      ))}
                    </div>
                  </div>
                )}
                {selectedTalent.headshot_url && (
                  <div style={{ marginTop: '1rem' }}>
                    <strong>Headshot:</strong>
                    <img 
                      src={selectedTalent.headshot_url} 
                      alt={selectedTalent.name}
                      style={{ width: '100%', maxWidth: '200px', marginTop: '0.5rem', borderRadius: '4px' }}
                    />
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button
                  onClick={() => openEditModal(selectedTalent)}
                  className="btn btn-secondary"
                  data-testid="modal-edit-btn"
                >
                  <Edit size={18} /> Edit
                </button>
                {selectedTalent.status === 'pending' && (
                  <>
                    <button
                      onClick={() => updateStatus(selectedTalent.id, 'approved')}
                      className="btn btn-primary"
                      style={{ backgroundColor: 'var(--success)' }}
                      data-testid="modal-approve-btn"
                    >
                      <Check size={18} /> Approve
                    </button>
                    <button
                      onClick={() => updateStatus(selectedTalent.id, 'rejected')}
                      className="btn btn-secondary"
                      style={{ borderColor: 'var(--error)', color: 'var(--error)' }}
                      data-testid="modal-reject-btn"
                    >
                      <X size={18} /> Reject
                    </button>
                  </>
                )}
                <button
                  onClick={() => setShowModal(false)}
                  className="btn btn-ghost"
                  data-testid="modal-close-btn"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add/Edit Modal */}
        {showAddModal && (
          <div className="modal-overlay" onClick={() => { setShowAddModal(false); setIsEditing(false); }} data-testid="add-talent-modal">
            <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
              <h2 className="modal-title">{isEditing ? 'Edit Talent' : 'Add New Talent'}</h2>
              
              <form onSubmit={isEditing ? handleEditTalent : handleAddTalent}>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleFormChange}
                      className="form-input"
                      required
                      data-testid="add-name-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleFormChange}
                      className="form-input"
                      required
                      disabled={isEditing}
                      data-testid="add-email-input"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleFormChange}
                      className="form-input"
                      data-testid="add-phone-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Representation Type</label>
                    <select
                      name="representation_type"
                      value={formData.representation_type}
                      onChange={handleFormChange}
                      className="form-select"
                      data-testid="add-type-select"
                    >
                      <option value="">Select...</option>
                      <option value="Actor">Actor</option>
                      <option value="Model">Model</option>
                      <option value="Voice Artist">Voice Artist</option>
                      <option value="Performer">Performer</option>
                      <option value="Multi-Talent">Multi-Talent</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Age Range</label>
                    <select
                      name="age_range"
                      value={formData.age_range}
                      onChange={handleFormChange}
                      className="form-select"
                      data-testid="add-age-select"
                    >
                      <option value="">Select...</option>
                      <option value="18-25">18-25</option>
                      <option value="25-35">25-35</option>
                      <option value="35-45">35-45</option>
                      <option value="45-55">45-55</option>
                      <option value="55+">55+</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Height</label>
                    <input
                      type="text"
                      name="height"
                      value={formData.height}
                      onChange={handleFormChange}
                      className="form-input"
                      placeholder="e.g., 5'10&quot;"
                      data-testid="add-height-input"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Bio / Credits</label>
                  <textarea
                    name="bio"
                    value={formData.bio}
                    onChange={handleFormChange}
                    className="form-textarea"
                    placeholder="Bio, experience, commercial credits, film work..."
                    style={{ minHeight: '100px' }}
                    data-testid="add-bio-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Skills</label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <input
                      type="text"
                      value={skillInput}
                      onChange={(e) => setSkillInput(e.target.value)}
                      className="form-input"
                      placeholder="Add a skill..."
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                      data-testid="add-skill-input"
                    />
                    <button type="button" onClick={addSkill} className="btn btn-secondary" style={{ padding: '0.5rem 1rem' }}>
                      <Plus size={18} />
                    </button>
                  </div>
                  {formData.skills.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                      {formData.skills.map((skill, idx) => (
                        <span key={idx} className="skill-item">
                          {skill}
                          <button type="button" onClick={() => removeSkill(skill)} className="skill-remove">
                            <X size={14} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Headshot URL</label>
                  <input
                    type="url"
                    name="headshot_url"
                    value={formData.headshot_url}
                    onChange={handleFormChange}
                    className="form-input"
                    placeholder="https://..."
                    data-testid="add-headshot-input"
                  />
                  {formData.headshot_url && (
                    <img 
                      src={formData.headshot_url} 
                      alt="Preview" 
                      style={{ width: '100px', height: '120px', objectFit: 'cover', marginTop: '0.5rem', borderRadius: '4px' }}
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  )}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Demo Reel URL</label>
                    <input
                      type="url"
                      name="demo_reel_url"
                      value={formData.demo_reel_url}
                      onChange={handleFormChange}
                      className="form-input"
                      placeholder="YouTube, Vimeo..."
                      data-testid="add-demoreel-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Instagram</label>
                    <input
                      type="text"
                      name="instagram"
                      value={formData.instagram}
                      onChange={handleFormChange}
                      className="form-input"
                      placeholder="@username"
                      data-testid="add-instagram-input"
                    />
                  </div>
                </div>

                <div className="modal-actions">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={submitting}
                    data-testid="save-talent-btn"
                  >
                    {submitting ? 'Saving...' : (isEditing ? 'Update Talent' : 'Add Talent')}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowAddModal(false); setIsEditing(false); }}
                    className="btn btn-ghost"
                    data-testid="cancel-add-btn"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
